import React, { useCallback } from "react"

import { Button, toast } from "@coval/ui"

import { MAX_TAGS } from "../constants/tag-constraints"
import { useBulkTagging } from "../hooks/useBulkTagging"
import { useOrganizationTags } from "../routes/useOrganizationTags"
import type { TaggableRecord } from "../utils/bulkTags"
import { AddTagIcon } from "./TagActionIcons"
import { TagEditor } from "./TagEditor"

/** Tags beyond this collapse into a "+N" count inside the trigger button. */
const MAX_TRIGGER_TAGS = 3

/** The words a surface's toasts use for one and many of its records. */
interface BulkTagNoun {
  singular: string
  plural: string
}

interface BulkTagEditorProps {
  /**
   * Invalidation for the surfaces that read the rewritten records. The
   * organization tag catalog is invalidated alongside it, so a caller only
   * names the surfaces its own resource owns.
   */
  invalidateTagged: (ids: string[]) => Promise<void>
  isDisabled?: boolean
  /** Rows for the selected records the list is currently carrying. */
  loadedRecords: readonly TaggableRecord[]
  noun: BulkTagNoun
  /** Every selected record, including any whose row has left the list. */
  recordIds: readonly string[]
  /**
   * The resource the selected records belong to, e.g. `"agents"`. Namespaces
   * the per-record write queue that keeps consecutive edits in order.
   */
  resource: string
  /** Writes one record's whole tag list. */
  updateTags: (id: string, tags: string[]) => Promise<unknown>
}

function pluralize(noun: BulkTagNoun, count: number): string {
  return count === 1 ? noun.singular : noun.plural
}

/**
 * Tag assignment for a multi-row selection, through the same control the
 * single-record surfaces use. The menu checks the tags the whole selection
 * already shares, so checking one adds it to every selected record and
 * unchecking one takes it off all of them, while a tag only some of them carry
 * is left where it is.
 */
export const BulkTagEditor: React.FC<BulkTagEditorProps> = ({
  invalidateTagged,
  isDisabled = false,
  loadedRecords,
  noun,
  recordIds,
  resource,
  updateTags,
}) => {
  const { suggestions, invalidate: invalidateOrgTags } = useOrganizationTags()

  // A bulk edit can mint a tag the catalog has never seen, so the catalog is
  // refreshed with the rows that now carry it.
  const invalidateTaggedAndCatalog = useCallback(
    async (ids: string[]) => {
      await Promise.all([invalidateOrgTags(), invalidateTagged(ids)])
    },
    [invalidateOrgTags, invalidateTagged],
  )

  const { applyTags, sharedTags } = useBulkTagging({
    invalidateTagged: invalidateTaggedAndCatalog,
    loadedRecords,
    recordIds,
    resource,
    updateTags,
  })

  const handleTagsChange = useCallback(
    (nextTags: string[]) => {
      void applyTags(nextTags).then(
        ({ failedCount, saturatedCount, updatedCount }) => {
          const saturatedDescription =
            saturatedCount > 0
              ? `${saturatedCount} ${pluralize(
                  noun,
                  saturatedCount,
                )} already at the ${MAX_TAGS}-tag limit ${
                  saturatedCount === 1 ? "was" : "were"
                } skipped.`
              : undefined

          if (failedCount > 0) {
            toast.danger({
              title: `Could not update tags on ${failedCount} of ${
                failedCount + updatedCount
              } ${noun.plural}`,
              description: saturatedDescription,
            })
            return
          }

          if (updatedCount > 0) {
            toast.success({
              title: `Updated tags on ${updatedCount} ${pluralize(
                noun,
                updatedCount,
              )}`,
              description: saturatedDescription,
            })
            return
          }

          if (saturatedCount > 0) {
            toast.warning({
              title: `No tags added — ${saturatedCount} ${pluralize(
                noun,
                saturatedCount,
              )} already at the ${MAX_TAGS}-tag limit`,
            })
          }
        },
      )
    },
    [applyTags, noun],
  )

  return (
    <TagEditor
      tags={sharedTags}
      onTagsChange={handleTagsChange}
      suggestions={suggestions}
      collapseOverflow
      trigger={
        <Button
          type="button"
          variant="secondary"
          icon
          tooltip="Add tag"
          aria-label="Add tag"
        >
          <AddTagIcon />
        </Button>
      }
      maxOverflowDots={MAX_TRIGGER_TAGS}
      allowColorEdit
      disabled={isDisabled}
    />
  )
}
